import React from 'react';
import { motion } from 'framer-motion';
import { Globe, MessageCircle, Database, Calendar } from 'lucide-react';
const chipVariants = {
  hidden: {
    opacity: 0,
    scale: 0.94
  },
  show: {
    opacity: 1,
    scale: 1
  }
};
interface ToolChipProps {
  label: string;
  icon: React.ComponentType<{
    size?: number;
    strokeWidth?: number;
  }>;
  delay: number;
}
function ToolChip({
  label,
  icon: Icon,
  delay
}: ToolChipProps) {
  return <motion.div variants={chipVariants} initial="hidden" whileInView="show" viewport={{
    once: true,
    amount: 0.4
  }} transition={{
    duration: 0.5,
    ease: [0.16, 1, 0.3, 1],
    delay
  }} className="relative z-10 flex items-center gap-2 bg-arena border border-carbon/10 rounded-[12px] px-3.5 py-2.5 shadow-card whitespace-nowrap" data-id="element-157" data-mp="1k10gah6op8tb">
      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-verde/10 text-verde flex-shrink-0" data-id="element-158" data-mp="22z39z9a6ftxg">
        <Icon size={14} strokeWidth={2} data-id="element-159" data-mp="27q1p45jfilz4" />
      </span>
      <span className="font-body text-[13px] font-medium text-carbon" data-id="element-160" data-mp="27rmp4aoiix4d">
        {label}
      </span>
    </motion.div>;
}
export function Problem() {
  return <section className="bg-arena py-[72px] md:py-[120px]" data-id="element-161" data-mp="13ri7id0bls15">
      <div className="max-w-[1200px] mx-auto px-6 md:px-20" data-id="element-162" data-mp="1hhu4oqbrigz5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-163" data-mp="1hj55ch9wv8m1">
          {/* Left: copy */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} data-id="element-164" data-mp="1cp0wttkzysiu">
            <span className="block font-body text-[11px] uppercase font-medium text-salvia tracking-widest mb-6" data-id="element-165" data-mp="1wd07z5jh41os">
              EL PROBLEMA
            </span>
            <h2 className="font-display text-[32px] md:text-[44px] font-bold text-carbon leading-[1.1] mb-6 max-w-[480px]" data-id="element-166" data-mp="2251q9vrgyp6">
              Tu empresa ya tiene herramientas. El problema es que no
              trabajan juntas.
            </h2>
            <p className="font-body text-[16px] text-carbon/60 leading-[1.7] max-w-[440px] mb-6" data-id="element-167" data-mp="2670wxfkv3nwc">
              Una web genera interés. WhatsApp recibe mensajes. El CRM
              guarda contactos. El calendario organiza citas. Pero alguien
              termina conectando todo manualmente.
            </p>
            <p className="font-display text-[20px] font-bold text-verde" data-id="element-168" data-mp="2k2v49u5zm57c">
              ORIGEN conecta el proceso.
            </p>
          </motion.div>

          {/* Right: tools connecting to ORIGEN in a symmetric cross */}
          <div className="relative w-full max-w-[380px] mx-auto grid grid-cols-3 grid-rows-3 gap-3 md:gap-4 h-[280px] md:h-[300px]" data-id="element-169" data-mp="2yu53hozlsaa8">
            {/* Connecting lines — centered through the middle row/column */}
            <motion.div initial={{
            opacity: 0,
            scaleY: 0
          }} whileInView={{
            opacity: 1,
            scaleY: 1
          }} viewport={{
            once: true,
            amount: 0.4
          }} transition={{
            duration: 0.5,
            ease: 'easeOut',
            delay: 0.15
          }} className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 origin-center" style={{
            backgroundImage: 'repeating-linear-gradient(to bottom, rgba(33,77,67,0.35) 0, rgba(33,77,67,0.35) 4px, transparent 4px, transparent 8px)'
          }} aria-hidden="true" data-id="element-170" data-mp="37tidqjvjx2w0" />
            <motion.div initial={{
            opacity: 0,
            scaleX: 0
          }} whileInView={{
            opacity: 1,
            scaleX: 1
          }} viewport={{
            once: true,
            amount: 0.4
          }} transition={{
            duration: 0.5,
            ease: 'easeOut',
            delay: 0.15
          }} className="absolute top-1/2 left-0 right-0 h-px -translate-y-1/2 origin-center" style={{
            backgroundImage: 'repeating-linear-gradient(to right, rgba(33,77,67,0.35) 0, rgba(33,77,67,0.35) 4px, transparent 4px, transparent 8px)'
          }} aria-hidden="true" data-id="element-171" data-mp="1ax5q0ozz9nbj" />

            {/* Row 1 */}
            <div data-id="element-172" data-mp="1ic6awyrr5t70" />
            <div className="flex items-center justify-center" data-id="element-173" data-mp="14ehrzwh8ww4l">
              <ToolChip label="Web" icon={Globe} delay={0.2} data-id="element-174" data-mp="14gccj9olum85" />
            </div>
            <div data-id="element-175" data-mp="19edtnp2h0bgz" />

            {/* Row 2 */}
            <div className="flex items-center justify-center" data-id="element-176" data-mp="1x36tvxq4l5he">
              <ToolChip label="WhatsApp" icon={MessageCircle} delay={0.28} data-id="element-177" data-mp="1x3oxxgj14nzv" />
            </div>
            <div className="flex items-center justify-center" data-id="element-178" data-mp="221qhe2kdapbv">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true,
              amount: 0.4
            }} transition={{
              duration: 0.5,
              ease: 'easeOut',
              delay: 0.45
            }} className="relative z-10 flex items-center justify-center w-16 h-16 rounded-full bg-verde text-arena font-display font-bold text-[13px] shadow-cta flex-shrink-0" data-id="element-179" data-mp="1ndvpupi3ir0d">
                ORIGEN
              </motion.div>
            </div>
            <div className="flex items-center justify-center" data-id="element-180" data-mp="4dwdrn4dkimg">
              <ToolChip label="Calendario" icon={Calendar} delay={0.28} data-id="element-181" data-mp="i5ueb9k0t670" />
            </div>

            {/* Row 3 */}
            <div data-id="element-182" data-mp="e48x5c0ge5r0" />
            <div className="flex items-center justify-center" data-id="element-183" data-mp="e5scn0eaccrq">
              <ToolChip label="CRM" icon={Database} delay={0.2} data-id="element-184" data-mp="3mjslbwm9oxz7" />
            </div>
            <div data-id="element-185" data-mp="3hl7n22qksode" />
          </div>
        </div>
      </div>
    </section>;
}